<template>
  <div class="student-turnover-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Student Turnover</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ campusRows.length }} Campuses
        </b-badge>
      </div>
      <div class="action-buttons">
        <button
          class="btn-modern btn-export"
          type="button"
          :disabled="exporting"
          @click="exportExcel()"
        >
          <feather-icon icon="DownloadIcon" size="18" class="mr-50" />
          <span>{{ exporting ? "Exporting..." : "Export Excel" }}</span>
        </button>
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading student turnover data...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-3">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Student Turnover</span>
              <span class="date-range-text ml-2">
                <strong>{{ periodLabel }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="turnover-title-box text-center mb-3">
        <div class="turnover-main-title">
          Students Withdrawal Summary Reason Wise
        </div>
        <div class="turnover-sub-title">
          For the period of {{ periodLabel }}
        </div>
      </div>

      <div class="table-scroll">
        <table class="turnover-table mb-2">
          <thead>
            <tr>
              <th class="campus-col text-left">Reasons</th>
              <th
                v-for="reason in reasonColumns"
                :key="'h-' + reason.id"
                class="num-col text-center"
              >
                {{ reason.id }}
              </th>
              <th class="num-col text-center total-col">Total</th>
              <th class="strength-col text-center">Strength</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in campusRows" :key="'r-' + idx">
              <td class="campus-col branch-cell">
                {{ row.campusName }}
              </td>
              <td
                v-for="reason in reasonColumns"
                :key="'c-' + idx + '-' + reason.id"
                class="num-col text-center"
              >
                {{ displayCount(row.counts[reason.id]) }}
              </td>
              <td class="num-col text-center font-weight-bold total-col">
                {{ displayCount(row.total) }}
              </td>
              <td class="strength-col text-center">
                {{ displayCount(row.studentStrength) }}
              </td>
            </tr>
            <tr class="total-row">
              <td class="campus-col branch-cell branch-total">Total</td>
              <td
                v-for="reason in reasonColumns"
                :key="'t-' + reason.id"
                class="num-col text-center font-weight-bold"
              >
                {{ displayCount(grandCounts[reason.id]) }}
              </td>
              <td class="num-col text-center font-weight-bold total-col">
                {{ displayCount(grandTotal) }}
              </td>
              <td class="strength-col text-center font-weight-bold">
                {{ displayCount(grandStrength) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pct-label font-weight-bold mb-1">Percentage</div>
      <div class="table-scroll mb-4">
        <table class="turnover-table">
          <thead>
            <tr>
              <th class="campus-col text-left">Reasons</th>
              <th
                v-for="reason in reasonColumns"
                :key="'ph-' + reason.id"
                class="num-col text-center"
              >
                {{ reason.id }}
              </th>
              <th class="num-col text-center total-col">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in campusRows" :key="'p-' + idx">
              <td class="campus-col branch-cell">
                {{ row.campusName }}
              </td>
              <td
                v-for="reason in reasonColumns"
                :key="'pc-' + idx + '-' + reason.id"
                class="num-col text-center"
              >
                {{ formatPercent(row.counts[reason.id], row.total) }}
              </td>
              <td class="num-col text-center font-weight-bold total-col">
                {{ formatPercent(row.total, row.total) }}
              </td>
            </tr>
            <tr class="total-row">
              <td class="campus-col branch-cell branch-total">Total</td>
              <td
                v-for="reason in reasonColumns"
                :key="'pt-' + reason.id"
                class="num-col text-center font-weight-bold"
              >
                {{ formatPercent(grandCounts[reason.id], grandTotal) }}
              </td>
              <td class="num-col text-center font-weight-bold total-col">
                {{ formatPercent(grandTotal, grandTotal) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="reason-key-title text-center font-weight-bold mb-2">
        STUDENT WITHDRAWAL REASONS KEY
      </div>
      <table class="reason-key-table">
        <tbody>
          <tr v-for="(pair, idx) in reasonKeyPairs" :key="'k-' + idx">
            <td class="key-num">{{ pair.left.id }}</td>
            <td class="key-text">{{ pair.left.title }}</td>
            <td class="key-num">{{ pair.right ? pair.right.id : "" }}</td>
            <td class="key-text">{{ pair.right ? pair.right.title : "" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

/** Standard IUSS withdrawal reason sequence (columns 1–18) */
const REASON_COLUMNS = [
  { id: 1, title: "Shifting To another city" },
  { id: 2, title: "Migration to another Country" },
  { id: 3, title: "Due to Financial Problems" },
  { id: 4, title: "Due to illness" },
  { id: 5, title: "Due to Conveyance problems" },
  { id: 6, title: "High fee structure" },
  { id: 7, title: "Parents Unaware of rules" },
  { id: 8, title: "Moving to another school" },
  { id: 9, title: "Family Problem" },
  { id: 10, title: "IUSS Staff Resigned" },
  { id: 11, title: "Not satisfied—infra structure/facilities" },
  { id: 12, title: "Not satisfied—school discipline" },
  { id: 13, title: "Not Satisfied—teachers attitude" },
  { id: 14, title: "Other reasons" },
  { id: 15, title: "Non-payment of fee" },
  { id: 16, title: "Prolonged absence" },
  { id: 17, title: "Family shifted abroad" },
  { id: 18, title: "Death of student / Legal Issues" },
];

/** Map API category text → reason seq no (1–18) */
const CATEGORY_ALIASES = [
  { match: /shift.*city|another city/i, id: 1 },
  { match: /migrat.*country|another country/i, id: 2 },
  { match: /financial|fee problem/i, id: 3 },
  { match: /illness|ill\b/i, id: 4 },
  { match: /conveyance/i, id: 5 },
  { match: /high fee|fee structure/i, id: 6 },
  { match: /unaware.*rule|parents unaware/i, id: 7 },
  { match: /moving.*school|another school|completed school/i, id: 8 },
  { match: /family problem/i, id: 9 },
  { match: /staff resigned|iuss staff/i, id: 10 },
  { match: /infra|facilit/i, id: 11 },
  { match: /discipline/i, id: 12 },
  { match: /teacher.*attitude|teachers attitude/i, id: 13 },
  { match: /non.?payment|non payment/i, id: 15 },
  { match: /prolonged absence|absence/i, id: 16 },
  { match: /shifted abroad|abroad/i, id: 17 },
  { match: /death|legal issue/i, id: 18 },
  { match: /co-education|not coping|other/i, id: 14 },
];

export default {
  name: "StudentTurnoverPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      year: new Date().getFullYear(),
      monthFrom: 1,
      monthTo: 12,
      categoryType: "leavingCategory",
      reasonColumns: REASON_COLUMNS,
      campusRows: [],
      branchLookup: {},
      autoExport: false,
      exporting: false,
    };
  },
  computed: {
    periodLabel() {
      const fromName = this.monthName(this.monthFrom);
      const toName = this.monthName(this.monthTo);
      if (this.monthFrom === this.monthTo) {
        return `${fromName} ${this.year}`;
      }
      return `${fromName} - ${toName} ${this.year}`;
    },
    grandCounts() {
      const totals = {};
      REASON_COLUMNS.forEach((r) => {
        totals[r.id] = 0;
      });
      this.campusRows.forEach((row) => {
        REASON_COLUMNS.forEach((r) => {
          totals[r.id] += Number(row.counts[r.id]) || 0;
        });
      });
      return totals;
    },
    grandTotal() {
      return this.campusRows.reduce(
        (sum, row) => sum + (Number(row.total) || 0),
        0
      );
    },
    grandStrength() {
      return this.campusRows.reduce(
        (sum, row) => sum + (Number(row.studentStrength) || 0),
        0
      );
    },
    reasonKeyPairs() {
      const pairs = [];
      for (let i = 0; i < REASON_COLUMNS.length; i += 2) {
        pairs.push({
          left: REASON_COLUMNS[i],
          right: REASON_COLUMNS[i + 1] || null,
        });
      }
      return pairs;
    },
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    async initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || 0;
      this.year = Number(query.year) || new Date().getFullYear();
      this.monthFrom = Number(query.monthFrom) || 1;
      this.monthTo = Number(query.monthTo) || 12;
      this.categoryType = query.categoryType || "leavingCategory";
      this.autoExport = ["1", "true", true].includes(query.export);

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      await this.loadCampusInfo();
      await this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const lookup = {};
          response.data.data.forEach((campus) => {
            const label =
              campus.branchName || campus.branch || campus.name || "";
            if (campus.id != null && label) {
              lookup[campus.id] = label;
            }
          });
          this.branchLookup = lookup;

          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch =
              this.cID == 0 || this.cID == 1
                ? "All Campuses"
                : match.branchName || match.branch || "";
            this.schoolLogo = match.logo;
          } else if (response.data.data.length) {
            const info = response.data.data[0];
            this.campusName = info.name;
            this.campusBranch = "All Campuses";
            this.schoolLogo = info.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed", e);
      }
    },
    resolveBranchLabel(source, fallback = "-") {
      if (!source || typeof source !== "object") return fallback;
      const label =
        source.branchName ||
        source.BranchName ||
        source.branch_name ||
        source.campusName ||
        source.branch ||
        source.campus ||
        source.name ||
        source.campusBranch ||
        (source.campusID != null ? this.branchLookup[source.campusID] : "") ||
        (source.id != null ? this.branchLookup[source.id] : "") ||
        "";
      return String(label).trim() || fallback;
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/Students/StudentTurnoverReport",
          {
            params: {
              db: this.db,
              campusID: this.cID,
              year: this.year,
              monthFrom: this.monthFrom,
              monthTo: this.monthTo,
              categoryType: this.categoryType,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = this.extractPayload(response.data);
        if (!payload) {
          this.error =
            response.data?.message ||
            "The server returned an unexpected response format.";
          return;
        }

        if (payload.year) this.year = Number(payload.year);
        if (payload.monthFrom) this.monthFrom = Number(payload.monthFrom);
        if (payload.monthTo) this.monthTo = Number(payload.monthTo);

        this.normalizeReport(payload);

        if (payload.branchName && !this.campusBranch) {
          this.campusBranch = payload.branchName;
        }
      } catch (err) {
        console.error("Student turnover report error:", err);
        this.error = "Failed to retrieve report data.";
      } finally {
        this.loading = false;
        if (this.autoExport && !this.error) {
          this.$nextTick(() => this.exportExcel(true));
        }
      }
    },
    extractPayload(data) {
      if (!data) return null;
      if (data.status === "success" && data.data) return data.data;
      if (typeof data === "object" && !Array.isArray(data)) return data;
      return null;
    },
    normalizeReport(payload) {
      const blocks = this.extractCampusBlocks(payload);

      if (!blocks.length) {
        this.campusRows = [];
        return;
      }

      this.campusRows = blocks.map((block) => this.buildCampusRow(block));
      this.applyBranchLookupToRows();
    },
    applyBranchLookupToRows() {
      this.campusRows = this.campusRows.map((row) => {
        if (row.campusName && row.campusName !== "-") return row;
        const label =
          (row.campusID != null && this.branchLookup[row.campusID]) ||
          row.campusName;
        return {
          ...row,
          campusName: label && label !== "-" ? label : row.campusName,
        };
      });
    },
    extractCampusBlocks(payload) {
      const listKeys = [
        "campuses",
        "branches",
        "campusSummary",
        "branchSummary",
        "campusRows",
        "campusData",
        "details",
      ];

      for (const key of listKeys) {
        if (Array.isArray(payload[key]) && payload[key].length) {
          return payload[key];
        }
      }

      // Flat summary rows tagged with campus — group into campus blocks
      if (Array.isArray(payload.summary) && payload.summary.length) {
        const hasCampusTag = payload.summary.some(
          (item) =>
            item.branchName ||
            item.campusName ||
            item.branch ||
            item.campus ||
            item.campusID != null
        );

        if (hasCampusTag && payload.includeAllCampuses) {
          return this.groupSummaryByCampus(payload.summary);
        }

        return [
          {
            campusID: payload.campusID,
            branchName:
              payload.branchName || payload.campusName || payload.branch,
            campusName:
              payload.branchName ||
              payload.campusName ||
              payload.branch ||
              (payload.includeAllCampuses ? "All Campuses" : this.campusBranch),
            studentStrength:
              payload.studentStrength ??
              payload.totalStudentStrength ??
              payload.strength ??
              0,
            summary: payload.summary,
          },
        ];
      }

      return [];
    },
    groupSummaryByCampus(summary) {
      const map = {};

      summary.forEach((item) => {
        const key =
          item.campusID ??
          item.branchName ??
          item.campusName ??
          item.branch ??
          item.campus ??
          "unknown";
        if (!map[key]) {
          map[key] = {
            campusID: item.campusID,
            branchName:
              item.branchName || item.campusName || item.branch || item.campus,
            campusName:
              item.branchName ||
              item.campusName ||
              item.branch ||
              item.campus ||
              String(key),
            studentStrength:
              item.studentStrength ?? item.strength ?? item.totalStrength ?? 0,
            summary: [],
          };
        }
        if (item.studentStrength != null && !map[key].studentStrength) {
          map[key].studentStrength = item.studentStrength;
        }
        map[key].summary.push(item);
      });

      return Object.values(map);
    },
    buildCampusRow(block) {
      const counts = {};
      REASON_COLUMNS.forEach((r) => {
        counts[r.id] = 0;
      });

      const summary = block.summary || block.categories || block.items || [];
      if (Array.isArray(summary)) {
        summary.forEach((item) => {
          const seqId = this.resolveReasonSeq(item);
          const amount = this.sumCategoryInSelectedMonths(item);
          counts[seqId] = (counts[seqId] || 0) + amount;
        });
      }

      const total = Object.values(counts).reduce(
        (sum, n) => sum + (Number(n) || 0),
        0
      );

      return {
        campusID: block.campusID ?? block.id ?? null,
        campusName: this.resolveBranchLabel(block),
        studentStrength: Number(
          block.studentStrength ??
            block.strength ??
            block.totalStrength ??
            block.studentCount ??
            0
        ),
        counts,
        total,
      };
    },
    resolveReasonSeq(item) {
      const categoryId = Number(item.categoryID ?? item.categoryId ?? 0);
      if (categoryId >= 1 && categoryId <= 18) return categoryId;

      const name = String(item.category || item.title || item.name || "")
        .trim()
        .toLowerCase();

      if (!name) return 14;

      for (const reason of REASON_COLUMNS) {
        const key = reason.title.toLowerCase();
        if (name === key || name.includes(key) || key.includes(name)) {
          return reason.id;
        }
      }

      for (const alias of CATEGORY_ALIASES) {
        if (alias.match.test(name)) return alias.id;
      }

      return 14;
    },
    sumCategoryInSelectedMonths(item) {
      const from = this.monthFrom;
      const to = this.monthTo;
      let sum = 0;

      if (item.months && typeof item.months === "object") {
        for (let m = from; m <= to; m++) {
          sum += Number(item.months[String(m)] ?? item.months[m] ?? 0) || 0;
        }
        return sum;
      }

      if (Array.isArray(item.monthCounts)) {
        for (let m = from; m <= to; m++) {
          sum += Number(item.monthCounts[m - 1]) || 0;
        }
        return sum;
      }

      return Number(item.total) || 0;
    },
    monthName(num) {
      const names = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return names[(Number(num) || 1) - 1] || "";
    },
    displayCount(val) {
      const n = Number(val);
      return Number.isNaN(n) ? 0 : n;
    },
    formatPercent(part, whole) {
      const p = Number(part) || 0;
      const w = Number(whole) || 0;
      if (!w) return "0.00%";
      return `${((p / w) * 100).toFixed(2)}%`;
    },
    exportCountValue(val) {
      const n = Number(val);
      return Number.isNaN(n) ? 0 : n;
    },
    buildCountHeaderRow() {
      return [
        "Reasons",
        ...REASON_COLUMNS.map((r) => r.id),
        "Total",
        "Student Strength",
      ];
    },
    buildCountDataRow(row) {
      return [
        row.campusName,
        ...REASON_COLUMNS.map((r) => this.exportCountValue(row.counts[r.id])),
        this.exportCountValue(row.total),
        this.exportCountValue(row.studentStrength),
      ];
    },
    buildPercentDataRow(row) {
      return [
        row.campusName,
        ...REASON_COLUMNS.map((r) =>
          this.formatPercent(row.counts[r.id], row.total)
        ),
        this.formatPercent(row.total, row.total),
      ];
    },
    buildExportRows() {
      const rows = [];
      const schoolTitle = this.campusBranch
        ? `${this.campusName} - ${this.campusBranch}`
        : this.campusName;

      rows.push([schoolTitle]);
      rows.push(["Student Turnover", this.periodLabel]);
      rows.push([]);
      rows.push(["Students Withdrawal Summary Reason Wise"]);
      rows.push([`For the period of ${this.periodLabel}`]);
      rows.push([]);
      rows.push(this.buildCountHeaderRow());
      this.campusRows.forEach((row) => {
        rows.push(this.buildCountDataRow(row));
      });
      rows.push([
        "Total",
        ...REASON_COLUMNS.map((r) =>
          this.exportCountValue(this.grandCounts[r.id])
        ),
        this.exportCountValue(this.grandTotal),
        this.exportCountValue(this.grandStrength),
      ]);
      rows.push([]);
      rows.push(["Percentage"]);
      rows.push(["Reasons", ...REASON_COLUMNS.map((r) => r.id), "Total"]);
      this.campusRows.forEach((row) => {
        rows.push(this.buildPercentDataRow(row));
      });
      rows.push([
        "Total",
        ...REASON_COLUMNS.map((r) =>
          this.formatPercent(this.grandCounts[r.id], this.grandTotal)
        ),
        this.formatPercent(this.grandTotal, this.grandTotal),
      ]);
      rows.push([]);
      rows.push(["STUDENT WITHDRAWAL REASONS KEY"]);
      rows.push(["No.", "Reason", "No.", "Reason"]);
      this.reasonKeyPairs.forEach((pair) => {
        rows.push([
          pair.left.id,
          pair.left.title,
          pair.right ? pair.right.id : "",
          pair.right ? pair.right.title : "",
        ]);
      });
      return rows;
    },
    async exportExcel(isAuto = false) {
      if (this.exporting) return;
      this.exporting = true;

      try {
        const XLSX = await import("xlsx");
        const rows = this.buildExportRows();
        const worksheet = XLSX.utils.aoa_to_sheet(rows);

        worksheet["!cols"] = [
          { wch: 14 },
          ...REASON_COLUMNS.map(() => ({ wch: 5 })),
          { wch: 7 },
          { wch: 8 },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Student Turnover");

        const fileName = `Student_Turnover_${this.year}_${this.monthFrom}-${this.monthTo}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        if (isAuto && window.opener) {
          setTimeout(() => window.close(), 600);
        }
      } catch (err) {
        console.error("Student turnover export error:", err);
        this.error = "Failed to export report.";
      } finally {
        this.exporting = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-turnover-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
}

.btn-export {
  background: linear-gradient(135deg, #00b894 0%, #55efc4 100%);
  color: white;
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #ffffff;
  padding: 2.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 16px;

  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.7rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }

  .report-info-line {
    font-size: 1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 12px;
      margin-right: 12px;
    }
  }
}

.turnover-title-box {
  border: 1px solid #333;
  padding: 10px 12px;
}

.turnover-main-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.turnover-sub-title {
  font-size: 0.95rem;
  margin-top: 4px;
}

.table-scroll {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.turnover-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.84rem;
  min-width: 920px;
  table-layout: fixed;
  background: #fff;

  th,
  td {
    border-right: 1px solid #cbd5e0;
    border-bottom: 1px solid #cbd5e0;
    padding: 8px 6px;
    vertical-align: middle;
  }

  th:first-child,
  td:first-child {
    border-left: 1px solid #cbd5e0;
  }

  thead tr:first-child th {
    border-top: 1px solid #cbd5e0;
  }

  thead th {
    background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
    font-weight: 700;
    color: #1a202c;
    font-size: 0.78rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tbody tr:nth-child(even):not(.total-row) td {
    background: #f8fafc;
  }

  tbody tr:hover:not(.total-row) td {
    background: #eef2ff;
  }

  .branch-cell {
    width: 88px;
    min-width: 88px;
    max-width: 88px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.15;
    font-size: 0.72rem;
    font-weight: 700;
    text-align: left;
    padding: 4px 3px;
    color: #1a202c;
    position: sticky;
    left: 0;
    z-index: 1;
    background: inherit;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
  }

  .branch-total {
    text-transform: uppercase;
    background: #e2e8f0 !important;
  }

  .campus-col {
    width: 88px;
    min-width: 88px;
    max-width: 88px;
  }

  .num-col {
    width: 44px;
    min-width: 44px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .total-col {
    background: #edf2f7;
    font-weight: 700;
  }

  .strength-col {
    width: 52px;
    min-width: 52px;
    max-width: 52px;
    background: #edf2f7;
    font-weight: 600;
    font-size: 0.74rem;
    font-variant-numeric: tabular-nums;
    padding: 4px 2px;
    line-height: 1.15;
  }

  .total-row td {
    background: #e2e8f0 !important;
    font-weight: 700;
    border-top: 2px solid #718096;
  }
}

.pct-label {
  font-size: 1rem;
  color: #2d3748;
  margin-top: 0.5rem;
  padding-left: 4px;
  border-left: 4px solid #6c5ce7;
}

.reason-key-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  overflow: hidden;

  td {
    border-right: 1px solid #cbd5e0;
    border-bottom: 1px solid #cbd5e0;
    padding: 7px 10px;
  }

  tr:last-child td {
    border-bottom: none;
  }

  td:last-child {
    border-right: none;
  }

  .key-num {
    width: 40px;
    text-align: center;
    font-weight: 700;
    background: #edf2f7;
    color: #4a5568;
  }

  .key-text {
    width: 45%;
    color: #2d3748;
  }
}

.reason-key-title {
  font-size: 0.95rem;
  letter-spacing: 0.03em;
}

.page-footer {
  text-align: center;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

@page {
  size: A4 landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  .student-turnover-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    padding: 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .turnover-table,
  .reason-key-table {
    font-size: 7pt !important;
    min-width: 0 !important;
  }

  .turnover-table {
    .branch-cell,
    .campus-col {
      width: 72px !important;
      min-width: 72px !important;
      max-width: 72px !important;
      font-size: 6.5pt !important;
      padding: 2px !important;
    }

    .strength-col {
      width: 42px !important;
      min-width: 42px !important;
      max-width: 42px !important;
      font-size: 6.5pt !important;
      padding: 2px !important;
    }

    th,
    td {
      padding: 2px 1px !important;
    }
  }

  .page-footer {
    display: none;
  }
}
</style>
